import React from 'react';
import { Box, Button, Card, CardContent, Chip, Divider, Typography } from '@mui/material';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { CommunicationSearchItem } from '../../../hooks/Marketing';
import {
    ACTION_STATUS_EXTRACTED,
    ACTION_STATUS_EXTRACTION_IN_PROGRESS,
    ACTION_STATUS_TERMINATED,
    ACTION_STATUS_TEST,
    ACTION_STATUS_WAITING_VALIDATION,
    formatDateTime,
    getChannelOption,
    getRemainingTime,
    isActionStatusEditable,
} from './constants';

interface CommunicationResultCardProps {
    data: CommunicationSearchItem;
    onDelete: (id: number) => void;
}

const CommunicationResultCard = ({ data, onDelete }: CommunicationResultCardProps) => {
    const channel = getChannelOption(data.defChannelId);
    const ChannelIcon = channel.icon;

    // Number(...) car l'API peut renvoyer actionStatusId sous forme de chaîne
    const actionStatusId = data.action?.actionStatusId != null ? Number(data.action.actionStatusId) : null;
    const isTerminated = actionStatusId === ACTION_STATUS_TERMINATED;
    const isWaitingValidation = actionStatusId === ACTION_STATUS_WAITING_VALIDATION;
    const isExtractionInProgress = actionStatusId === ACTION_STATUS_EXTRACTION_IN_PROGRESS;
    const isExtracted = actionStatusId === ACTION_STATUS_EXTRACTED;
    const isTestOperation = actionStatusId === ACTION_STATUS_TEST;
    const remaining = isWaitingValidation ? getRemainingTime(data.validUntil) : null;
    const isExpired = remaining?.expired ?? false;
    const canDelete = !isTestOperation && isActionStatusEditable(actionStatusId);
    const canFinalize = !isTestOperation && isActionStatusEditable(actionStatusId);

    return (
        <Card
            variant="outlined"
            sx={{
                borderRadius: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                ...(isExpired && { borderColor: 'error.main', borderWidth: 2 }),
            }}
        >
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1, '&:last-child': { pb: 2 } }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} noWrap title={data.animationInput?.title ?? undefined}>
                    {data.animationInput?.title ?? '—'}
                </Typography>

                <Divider />

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                        <FolderOutlinedIcon fontSize="small" sx={{ color: '#556ee6', flexShrink: 0 }} />
                        <Typography variant="body2" fontWeight={600} noWrap title={data.defTitle}>
                            {data.defTitle} (#{data.id})
                        </Typography>
                    </Box>
                    <ChannelIcon fontSize="small" titleAccess={channel.label} sx={{ color: '#6c757d', flexShrink: 0 }} />
                </Box>

                <Typography variant="caption" color="text.secondary">
                    Créé le {formatDateTime(data.creationDate)}
                </Typography>

                <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 1, pt: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minHeight: 24 }}>
                        {isTerminated && (
                            <Chip label="Envoyée" size="small" color="success" sx={{ fontSize: '0.7rem', height: 20 }} />
                        )}
                        {isExtractionInProgress && (
                            <Chip label="Extraction en cours" size="small" color="warning" sx={{ fontSize: '0.7rem', height: 20 }} />
                        )}
                        {isExtracted && (
                            <Chip label="Extraite" size="small" color="success" sx={{ fontSize: '0.7rem', height: 20 }} />
                        )}
                        {isTestOperation && (
                            <Chip label="Opération de test" size="small" color="info" sx={{ fontSize: '0.7rem', height: 20 }} />
                        )}
                        {isWaitingValidation && remaining && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <AccessTimeIcon fontSize="small" sx={{ color: isExpired ? '#dc3545' : '#28a745' }} />
                                <Typography variant="caption" sx={{ color: isExpired ? '#dc3545' : '#28a745', fontWeight: 600 }}>
                                    {remaining.label}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                        {canDelete ? (
                            <Button
                                size="small"
                                color="error"
                                variant="outlined"
                                startIcon={<DeleteOutlineIcon fontSize="small" />}
                                onClick={() => onDelete(data.id)}
                            >
                                Supprimer
                            </Button>
                        ) : (
                            <span />
                        )}

                        {(isTerminated || isExtracted || isTestOperation) && (
                            <Button size="small" variant="outlined" startIcon={<VisibilityIcon fontSize="small" />}>
                                Visualiser
                            </Button>
                        )}
                        {canFinalize && (
                            <Button size="small" variant="contained">
                                Finaliser
                            </Button>
                        )}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

export default CommunicationResultCard;
